'use client';
import * as React from 'react';
import { motion, useReducedMotion, type Transition, type Variants } from 'framer-motion';
import { cn, getTestId, type TestIdProps } from '../utils';

/** Vertical distance of the outer bars from the centre, in pixels. */
const BAR_OFFSET = 6;
/** Delay of the second morph phase so translate and rotate never run together. */
const PHASE_DELAY = 0.16;

const phase = (delay: number, reduceMotion: boolean): Transition =>
  reduceMotion ? { duration: 0 } : { duration: 0.2, delay, ease: [0.4, 0, 0.2, 1] };

// Outer bars: slide to the centre first, then rotate into the cross (reversed on close).
const outerBarVariants = (angle: number, reduceMotion: boolean): Variants => ({
  closed: {
    y: angle > 0 ? -BAR_OFFSET : BAR_OFFSET,
    rotate: 0,
    transition: { rotate: phase(0, reduceMotion), y: phase(PHASE_DELAY, reduceMotion) }
  },
  open: {
    y: 0,
    rotate: angle,
    transition: { y: phase(0, reduceMotion), rotate: phase(PHASE_DELAY, reduceMotion) }
  }
});

const middleBarVariants = (reduceMotion: boolean): Variants => ({
  closed: { opacity: 1, scaleX: 1, transition: phase(PHASE_DELAY, reduceMotion) },
  open: { opacity: 0, scaleX: 0.2, transition: phase(0, reduceMotion) }
});

export interface MenuToggleIconProps extends TestIdProps {
  /** Renders the cross when true, the hamburger when false. */
  open: boolean;
  className?: string;
}

/**
 * Hamburger icon that morphs into a cross. Animates on mount, so it can be
 * rendered in a drawer that opens already in the `open` state.
 */
export const MenuToggleIcon = React.forwardRef<HTMLSpanElement, MenuToggleIconProps>(
  ({ open, className, testId, id }, ref) => {
    const reduceMotion = useReducedMotion() ?? false;
    const state = open ? 'open' : 'closed';

    const bar = 'absolute left-0 top-[calc(50%-1px)] h-0.5 w-full rounded-full bg-current';

    return (
      <span
        ref={ref}
        data-testid={getTestId({ testId, id })}
        aria-hidden='true'
        className={cn('relative block size-5', className)}
      >
        <motion.span
          className={bar}
          initial='closed'
          animate={state}
          variants={outerBarVariants(45, reduceMotion)}
        />
        <motion.span
          className={bar}
          initial='closed'
          animate={state}
          variants={middleBarVariants(reduceMotion)}
        />
        <motion.span
          className={bar}
          initial='closed'
          animate={state}
          variants={outerBarVariants(-45, reduceMotion)}
        />
      </span>
    );
  }
);

MenuToggleIcon.displayName = 'MenuToggleIcon';
